/**
 * useScrollSync.ts — PLAT-10264 (T5, epic PLAT-10209)
 *
 * Manual cross-pane scroll mirroring for the Translation Review aligned-blocks
 * view. When the user drags one column, the other follows proportionally so the
 * original and translated rows stay roughly aligned even when their natural
 * heights differ.
 *
 * Net-new (the plan's "reuse the T4 scroll-sync helper" was wrong — no such
 * helper exists; the only shell scroll code is the open-time `scrollTop = 0`
 * reset). This is the AC "scroll one pane and the other follows."
 *
 * Scoping (CLAUDE.md #12): listeners are bound to the two element refs passed
 * in — never `document`-global queries — so a second transcript instance behind
 * the modal is untouched.
 *
 * Re-entrancy: writing `scrollTop` on the mirrored pane fires ITS scroll event,
 * which would write back and fight the user. A short syncing flag (cleared on
 * the next frame) suppresses the echo so only the user-driven pane leads.
 *
 * External suppression: a caller that programmatically scrolls BOTH panes
 * itself (e.g. `AlignedBlocksView`'s active-row auto-scroll, which moves each
 * column to its own active row) passes a `suppressRef`. While it is `true`,
 * mirroring is skipped so the two `scrollTo`/`scrollTop` writes don't fight
 * each other or get proportionally re-aligned. This is the SAME guard-the-echo
 * mechanism the internal `syncing` flag uses, just owned by the caller for the
 * duration of its own programmatic scroll.
 *
 * Proportional, not absolute: panes have different total heights, so we mirror
 * the scroll *fraction* (`scrollTop / maxScroll`) rather than the raw pixels.
 */

import { LogManager } from '@src/utility/Logger';
import { useEffect } from 'react';

import type React from 'react';

const logger = LogManager.getLogger('feature:TranslationReviewScrollSync');

/** Largest scrollable offset for an element (0 when it doesn't overflow). */
function maxScrollTop(el: HTMLElement): number {
  return Math.max(0, el.scrollHeight - el.clientHeight);
}

/**
 * Mirror vertical scroll between two containers proportionally.
 *
 * @param leftRef      one scroll container
 * @param rightRef     the other scroll container
 * @param enabled      when false, no listeners are attached (e.g. Paragraph mode)
 * @param suppressRef  optional; while `.current` is true, mirroring is skipped
 *                     so a caller can programmatically scroll BOTH panes (its
 *                     own active-row auto-scroll) without the columns fighting.
 */
export function useScrollSync(
  leftRef: React.RefObject<HTMLElement>,
  rightRef: React.RefObject<HTMLElement>,
  enabled: boolean,
  suppressRef?: React.RefObject<boolean>,
): void {
  useEffect(() => {
    if (!enabled) return;
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;

    // True while we are programmatically writing the *other* pane's scrollTop,
    // so its echoed scroll event is ignored instead of writing back.
    let syncing = false;
    let rafId: number | null = null;

    const mirror = (source: HTMLElement, target: HTMLElement) => {
      if (syncing || suppressRef?.current) return;
      const sourceMax = maxScrollTop(source);
      const targetMax = maxScrollTop(target);
      // Nothing to mirror to — target fits without scrolling.
      if (targetMax === 0) return;
      const fraction = sourceMax === 0 ? 0 : source.scrollTop / sourceMax;
      const next = fraction * targetMax;
      // Skip sub-pixel no-ops so we don't churn the rAF flag on every tick.
      if (Math.abs(target.scrollTop - next) < 0.5) return;
      syncing = true;
      target.scrollTop = next;
      // Release on the next frame — by then the echoed scroll event has fired
      // and been ignored. rAF (not a microtask) is the reliable point after
      // the synchronous scroll write's event has been dispatched.
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        syncing = false;
        rafId = null;
      });
    };

    const onLeftScroll = () => mirror(left, right);
    const onRightScroll = () => mirror(right, left);

    left.addEventListener('scroll', onLeftScroll, { passive: true });
    right.addEventListener('scroll', onRightScroll, { passive: true });

    // Align once on attach. At first mount both panes sit at 0 and this is a
    // no-op (the sub-pixel guard returns early). It earns its place when the
    // LEFT pane has just remounted — leaving the workspace's expanded-video
    // layout — because it comes back at scrollTop 0 while the right pane is
    // wherever the reviewer left it. Without this the two only re-align on the
    // next scroll gesture.
    mirror(right, left);
    logger.debug('scroll-sync attached');

    return () => {
      left.removeEventListener('scroll', onLeftScroll);
      right.removeEventListener('scroll', onRightScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // Re-bind when the panes (refs' current nodes can't be deps; the enabled
    // flag + mount covers the lifecycle — the refs are stable for the view's
    // lifetime). `enabled` flips when the user toggles into/out of an aligned
    // mode, which remounts this view, so this effect re-runs correctly.
    // `suppressRef` is a stable ref; listed for exhaustive-deps only.
  }, [enabled, leftRef, rightRef, suppressRef]);
}
